import EventEmitter from 'events';
import { padStart, sample } from 'lodash';
import { drawLabel, COLORS, LABEL_IMAGE_HEIGHT, LABEL_IMAGE_WIDTH } from './labelFactory';

const ffmpegUrl = '/encoder/ffmpeg-worker-mp4.js';

const FFMPEG_LOGGING = process.env.NODE_ENV !== 'production';

const BG_FPS = 60;
const VIDEO_WIDTH = LABEL_IMAGE_WIDTH / 2;
const VIDEO_HEIGHT = LABEL_IMAGE_HEIGHT / 2;

type Bgdata = {
    data: Blob,
    color: string
};

export class Encoder extends EventEmitter {
    private ffmpeg: FFMpeg;

    constructor(private recording: Blob, private duration: number, private seek: number) {
        super();
        this.ffmpeg = new FFMpeg();
    }

    async encode(artistName: string, trackName: string) {
        setTimeout(() => this.emit('progress', 0));
        let bg = await new Promise<Bgdata>(res => setTimeout(() => encodeBackground(artistName, trackName).then(res)));
        this.emit('progress', 0.1);
        let bgRotationPeriod = 60 / 33;
        let bgLoopDuration = bgRotationPeriod * 1000;
        let onBgProgress = (progress: number) => this.emit('progress', 0.1 + (progress / bgLoopDuration) * 0.8);
        this.ffmpeg.addListener('encodeProgress', onBgProgress);
        let bgLoop = await this.ffmpeg.generateBackgroundLoop(bg.data, bgLoopDuration, bg.color);
        this.ffmpeg.removeListener('encodeProgress', onBgProgress);
        let onFinalProgress = (progress: number) => this.emit('progress', 0.9 + (progress / (this.duration - this.seek)) * 0.1);
        this.ffmpeg.addListener('encodeProgress', onFinalProgress);
        let final = await this.ffmpeg.generateFinal(this.recording, bgLoop, this.duration - this.seek, this.seek);
        this.ffmpeg.removeListener('encodeProgress', onFinalProgress);
        this.ffmpeg.kill();
        return { video: final, poster: bg.data };
    }

    cancel() {
        this.ffmpeg.kill();
    }
}

export class FFMpeg extends EventEmitter {

    private worker: Worker;
    private ready: Promise<void>;

    constructor() {
        super();
        this.worker = new Worker(ffmpegUrl);
        this.ready = new Promise((readyRes, readyRej) => {
            let onMsg = (e: MessageEvent) => {
                let msg = e.data;
                switch (msg.type) {
                    case 'ready':
                        this.worker.postMessage({
                            type: "run",
                            arguments: ["-version"],
                            MEMFS: [],
                            TOTAL_MEMORY: 1048176 // 1Mb
                        });
                        break;
                    case 'stdout':
                        this.onStdout(msg.data);
                        break;
                    case 'stderr':
                        this.onStderr(msg.data);
                        break;
                    case 'done':
                        this.worker.removeEventListener('message', onMsg);
                        readyRes();
                        break;
                    case 'error':
                        this.worker.removeEventListener('message', onMsg);
                        readyRej(msg.data);
                        break;
                }
            }
            this.worker.addEventListener('message', onMsg);
        });

    }

    kill() {
        this.worker.terminate();
    }

    async generateBackgroundLoop(bgData: Blob, duration: number, bgColor: string): Promise<Blob> {
        await this.ready;

        let done = new Promise<Blob>((doneRes, doneRej) => {
            let onMsg = (e: MessageEvent) => {
                let msg = e.data;
                switch (msg.type) {
                    case 'stdout':
                        this.onStdout(msg.data);
                        break;
                    case 'stderr':
                        this.onStderr(msg.data);
                        break;
                    case 'exit':
                        this.onExit(msg.data);
                        break;
                    case 'done':
                        this.worker.removeEventListener('message', onMsg);
                        this.onDone(msg.data, doneRes, doneRej);
                        break;
                    case 'error':
                        this.worker.removeEventListener('message', onMsg);
                        this.onError(msg.data, doneRej);
                        break;
                }
            };
            this.worker.addEventListener('message', onMsg);
        });

        let bgInput = await new Response(bgData).arrayBuffer();
        let bgFilename = 'bg.png';
        this.worker.postMessage({
            type: "run",
            arguments: [
                "-loop",
                "1",
                "-r",
                `${BG_FPS}`,
                "-i",
                bgFilename,
                "-vf",
                `rotate=2*PI*t/${60 / 33}`,
                "-c:v",
                "libx264",
                "-f",
                "mp4",
                "-pix_fmt",
                "yuv420p",
                "-s",
                `${VIDEO_WIDTH}x${VIDEO_HEIGHT}`,
                "-r",
                `${BG_FPS}`,
                "-t",
                formatDurationString(duration),
                "bg.mp4"
            ],
            MEMFS: [
                { name: bgFilename, data: bgInput }
            ]
            ,
            TOTAL_MEMORY: 1073741824 // 1Gb
        });
        return done;
    }

    async generateFinal(audio: Blob, bgLoop: Blob, duration: number, audioInputSeek: number): Promise<Blob> {
        await this.ready;
        let audioInput = await new Response(audio).arrayBuffer();
        let bgInput = await new Response(bgLoop).arrayBuffer();
        let bgFilename = 'bg_in';
        let audioFilename = 'audio_in';

        let done = new Promise<Blob>((doneRes, doneRej) => {
            let onMsg = (e: MessageEvent) => {
                let msg = e.data;
                switch (msg.type) {
                    case 'stdout':
                        this.onStdout(msg.data);
                        break;
                    case 'stderr':
                        this.onStderr(msg.data);
                        break;
                    case 'exit':
                        this.onExit(msg.data);
                        break;
                    case 'done':
                        this.worker.removeEventListener('message', onMsg);
                        this.onDone(msg.data, doneRes, doneRej);
                        break;
                    case 'error':
                        this.worker.removeEventListener('message', onMsg);
                        this.onError(msg.data, doneRej);
                        break;
                }
            };
            this.worker.addEventListener('message', onMsg);
        });

        this.worker.postMessage({
            type: "run",
            arguments: [
                "-stream_loop",
                "-1",
                "-i",
                bgFilename,
                "-ss",
                formatDurationString(audioInputSeek),
                "-i",
                audioFilename,
                "-vcodec",
                "copy",
                "-t",
                formatDurationString(duration),
                "final.mp4"
            ],
            MEMFS: [
                { name: audioFilename, data: audioInput },
                { name: bgFilename, data: bgInput }
            ]
            ,
            TOTAL_MEMORY: 1073741824 // 1Gb
        });

        return done;
    }

    private onStdout(msg: string) {
        FFMPEG_LOGGING && console.log('ffmpeg stdout', msg);
    }

    private onStderr(msg: string) {
        FFMPEG_LOGGING && console.error('ffmpeg stderr', msg);
        let matchTime = /time=(\d+):(\d+):(\d+\.\d+)/.exec(msg);
        if (matchTime) {
            let [, hours, minutes, seconds] = matchTime;
            let time = ((((+hours * 60) + +minutes) * 60) + +seconds) * 1000;
            this.emit('encodeProgress', time);
        }
    }

    private onExit(code: number) {
        FFMPEG_LOGGING && console.log('ffmpeg exit', code);
    }

    private onDone(result: any, cb: (blob: Blob) => void, errCb: (err: any) => void) {
        if (result.MEMFS && result.MEMFS.length > 0 && result.MEMFS[0].data) {
            let data = result.MEMFS[0].data;
            let blob = new Blob([data], { type: 'video/mp4' });
            cb(blob);
        } else {
            errCb('No output from encoder');
        }
    }

    private onError(msg: string, cb: (err: any) => void) {
        cb(msg);
    }

}

function formatDurationString(millis: number) {
    let durationMinutes = Math.floor(millis / (1000 * 60));
    let durationSeconds = Math.floor((millis - (durationMinutes * 1000 * 60)) / 1000);
    let durationMillis = Math.floor(millis % 1000);
    return `00:${padStart('' + durationMinutes, 2, '0')}:${padStart('' + durationSeconds, 2, '0')}.${durationMillis}`;
}

function encodeBackground(artistName: string, trackName: string) {
    let canvas = document.createElement('canvas');
    canvas.width = LABEL_IMAGE_WIDTH;
    canvas.height = LABEL_IMAGE_HEIGHT;
    let context = canvas.getContext('2d')!;

    let { bg: backgroundColor, fg: foregroundColor } = sample(COLORS)!

    drawLabel(context, artistName, trackName, backgroundColor, foregroundColor);
    return new Promise<Bgdata>(res => {
        canvas.toBlob(b => res({ data: b!, color: backgroundColor }), 'image/png');
    });
}

